import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Sparkles, Zap, Plus, MessageSquare, BookOpen, Loader2 } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import api, { apiErr } from "@/lib/api";

function renderText(text) {
  const lines = (text || "").split("\n");
  return lines.map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i}>{line.slice(3)}</h2>;
    if (line.trim() === "---") return <hr key={i} />;
    let html = line
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (line.startsWith("- ") || line.startsWith("* "))
      return <li key={i} dangerouslySetInnerHTML={{ __html: html.slice(2) }} />;
    if (line.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function Chat() {
  const { conversationId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setCredits, refreshMeta } = useApp();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(params.get("project") || "none");
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [convId, setConvId] = useState(conversationId || null);
  const [input, setInput] = useState("");
  const [deep, setDeep] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadConvos(); }, [projectId]);
  useEffect(() => { if (conversationId) openConv(conversationId); }, [conversationId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const loadConvos = async () => {
    const q = projectId && projectId !== "none" ? `?project_id=${projectId}` : "";
    try { const r = await api.get(`/conversations${q}`); setConversations(r.data); } catch {}
  };

  const openConv = async (id) => {
    try { const r = await api.get(`/conversations/${id}`); setConvId(id); setMessages(r.data.messages || []); } catch {}
  };

  const newConv = () => { setConvId(null); setMessages([]); navigate("/app/chat"); };

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", {
        message: text, conversation_id: convId,
        project_id: projectId !== "none" ? projectId : null, deep,
      });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, sources: data.sources }]);
      setCredits(data.credits);
      loadConvos();
    } catch (e) {
      toast.error(apiErr(e));
      setMessages((m) => m.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
      refreshMeta();
    }
  };

  return (
    <div className="h-full flex">
      {/* Conversation list */}
      <div className="w-64 border-r border-[#E5E7EB] bg-white flex-col hidden lg:flex">
        <div className="p-4 border-b border-[#E5E7EB]">
          <Button onClick={newConv} className="w-full bg-navy text-white hover:bg-navy-800 rounded-lg gap-2 h-9" data-testid="new-conversation-btn"><Plus className="h-4 w-4" /> Nouvelle conversation</Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {conversations.length === 0 && <p className="text-xs text-muted2 p-3">Aucune conversation.</p>}
          {conversations.map((c) => (
            <button key={c.id} onClick={() => openConv(c.id)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm mb-1 truncate flex items-center gap-2 ${convId === c.id ? "bg-navy/5 text-navy font-medium" : "text-muted2 hover:bg-lisungi-bg"}`} data-testid={`conversation-${c.id}`}>
              <MessageSquare className="h-3.5 w-3.5 shrink-0" /> {c.title}
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col bg-white min-w-0">
        <div className="border-b border-[#E5E7EB] px-6 py-3 flex items-center gap-3">
          <span className="text-sm text-muted2 hidden sm:block">Projet actif :</span>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-56 h-9" data-testid="chat-project-select"><SelectValue placeholder="Aucun projet" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucun projet (général)</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          <div className="max-w-3xl mx-auto">
            {messages.length === 0 && !sending && (
              <div className="text-center py-16">
                <div className="h-14 w-14 rounded-2xl bg-navy mx-auto flex items-center justify-center"><Sparkles className="h-7 w-7 text-lisungi-yellow" /></div>
                <h2 className="font-serif text-2xl font-bold text-navy mt-5">Comment puis-je vous accompagner ?</h2>
                <p className="text-muted2 mt-2 text-sm max-w-md mx-auto">Posez une question méthodologique SIC. Lisungi consulte la base du LARSICOM lorsqu'elle est pertinente.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-8 max-w-xl mx-auto">
                  {["Comment construire une problématique de mémoire en SIC ?", "Aide-moi à formuler mes hypothèses de recherche.", "Quelle méthode pour une analyse de contenu ?", "Structure mon cadre méthodologique."].map((s) => (
                    <button key={s} onClick={() => setInput(s)} className="text-left text-sm p-3 rounded-xl border border-[#E5E7EB] hover:border-navy hover:bg-lisungi-bg transition-all text-ink">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`mb-6 flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <div className="h-8 w-8 rounded-lg bg-navy flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4 text-lisungi-yellow" /></div>}
                <div className={`${m.role === "user" ? "bg-navy text-white rounded-2xl rounded-br-md px-4 py-3 max-w-[80%]" : "chat-prose text-ink max-w-full"}`}>
                  {m.role === "user" ? <p className="whitespace-pre-wrap">{m.content}</p> : renderText(m.content)}
                  {m.sources?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {m.sources.map((s, j) => (
                        <span key={j} className="inline-flex items-center gap-1 text-[11px] font-mono bg-lisungi-yellow/15 text-navy px-2 py-1 rounded-md border border-lisungi-yellow/40"><BookOpen className="h-3 w-3" /> {s.doc_title}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && <div className="flex gap-3 mb-6"><div className="h-8 w-8 rounded-lg bg-navy flex items-center justify-center"><Sparkles className="h-4 w-4 text-lisungi-yellow" /></div><div className="flex items-center gap-2 text-muted2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Lisungi réfléchit…</div></div>}
            <div ref={bottomRef} />
          </div>
        </div>

        {/* Input */}
        <div className="border-t border-[#E5E7EB] px-4 sm:px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <Switch checked={deep} onCheckedChange={setDeep} id="deep" data-testid="deep-toggle" />
              <label htmlFor="deep" className="text-xs text-muted2 flex items-center gap-1 cursor-pointer"><Zap className="h-3.5 w-3.5 text-lisungi-yellow" /> Analyse approfondie (coût supérieur)</label>
            </div>
            <div className="relative">
              <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Écrivez votre question méthodologique…" rows={2}
                className="resize-none pr-14 rounded-xl border-[#E5E7EB] focus-visible:ring-navy" data-testid="chat-input" />
              <Button onClick={send} disabled={sending || !input.trim()} size="icon" className="absolute right-2 bottom-2 h-9 w-9 bg-navy text-white hover:bg-navy-800 rounded-lg" data-testid="chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-[11px] text-muted2 mt-2 text-center">Lisungi peut se tromper. Vérifiez les informations importantes et adaptez-les aux consignes de votre établissement.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
